import React from 'react';
import { createPortal } from 'react-dom';
import ReactPaginate from 'react-paginate';
import Select from 'react-select';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './CommonPagination.module.css';

interface CommonPaginationProps {
    show: boolean;
    pageCount: number;
    currentPage?: number;
    onPageChange: (selectedItem: { selected: number }) => void;
    totalItems?: number;
    itemsPerPage?: number;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    renderInPlace?: boolean;
}

const CommonPagination: React.FC<CommonPaginationProps> = ({
    show,
    pageCount,
    currentPage = 0,
    onPageChange,
    totalItems,
    itemsPerPage = 10,
    onItemsPerPageChange,
    renderInPlace = false
}) => {
    // Simplified portal target lookup
    const target = document.getElementById('pagination-target');

    if (!show || (!target && !renderInPlace)) return null;

    const startItem = currentPage * itemsPerPage + 1;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalItems || 0);

    const itemsPerPageOptions = [
        { value: 5, label: '5' },
        { value: 10, label: '10' },
        { value: 20, label: '20' },
        { value: 25, label: '25' },
        { value: 40, label: '40' },
        { value: 60, label: '60' },
        { value: 80, label: '80' },
        { value: 100, label: '100' }
    ];

    const isDark = document.documentElement.classList.contains('dark');

    const customStyles = {
        control: (provided: any) => ({
            ...provided,
            minHeight: '28px',
            height: '28px',
            fontSize: '11px',
            border: `1px solid ${isDark ? '#4b5563' : '#e2e8f0'}`,
            borderRadius: '6px',
            boxShadow: 'none',
            background: isDark ? '#374151' : 'white',
            '&:hover': {
                borderColor: isDark ? '#6b7280' : '#cbd5e1'
            }
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            height: '28px',
            padding: '0 6px',
            color: isDark ? '#f9fafb' : '#1e293b',
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: isDark ? '#f9fafb' : '#1e293b',
        }),
        input: (provided: any) => ({
            ...provided,
            margin: '0px',
            color: isDark ? '#f9fafb' : '#1e293b',
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        indicatorsContainer: (provided: any) => ({
            ...provided,
            height: '28px',
            color: isDark ? '#9ca3af' : '#64748b',
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: isDark ? '#9ca3af' : '#64748b',
            padding: '0 4px',
        }),
        menu: (provided: any) => ({
            ...provided,
            borderRadius: '8px',
            border: `1px solid ${isDark ? '#4b5563' : '#e2e8f0'}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
            background: isDark ? '#1f2937' : 'white',
        }),
        menuList: (provided: any) => ({
            ...provided,
            padding: '4px',
            borderRadius: '8px',
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            fontSize: '11px',
            padding: '6px 8px',
            borderRadius: '4px',
            backgroundColor: state.isSelected
                ? '#83cee4'
                : state.isFocused
                    ? (isDark ? '#374151' : '#f0f9ff')
                    : 'transparent',
            color: state.isSelected ? '#011926' : (isDark ? '#d1d5db' : '#64748b'),
            cursor: 'pointer',
            '&:active': { backgroundColor: '#83cee4' }
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
    };

    const content = (
        <div className={styles.container}>
            <div className={styles.left}>
                {totalItems !== undefined && (
                    <div className={styles.info}>
                        Showing {startItem}-{endItem} of {totalItems} results
                    </div>
                )}
                {onItemsPerPageChange && (
                    <div className={styles.itemsPerPage}>
                        <span className={styles.itemsPerPageLabel}>Per Page:</span>
                        <Select
                            value={itemsPerPageOptions.find(option => option.value === itemsPerPage)}
                            onChange={(option) => option && onItemsPerPageChange(option.value)}
                            options={itemsPerPageOptions}
                            styles={customStyles}
                            isSearchable={false}
                            className={styles.itemsPerPageSelect}
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                )}
            </div>

            {pageCount > 0 && (
                <div className={styles.center}>
                    <ReactPaginate
                        pageCount={pageCount}
                        pageRangeDisplayed={3}
                        marginPagesDisplayed={1}
                        onPageChange={onPageChange}
                        forcePage={currentPage}
                        containerClassName="pagination"
                        pageClassName="pagination-item"
                        pageLinkClassName="pagination-link"
                        activeClassName="pagination-active"
                        previousClassName="pagination-item"
                        nextClassName="pagination-item"
                        previousLinkClassName="pagination-link"
                        nextLinkClassName="pagination-link"
                        disabledClassName="pagination-disabled"
                        previousLabel={<ChevronLeft size={14} />}
                        nextLabel={<ChevronRight size={14} />}
                    />
                </div>
            )}
        </div>
    );

    if (renderInPlace) {
        return content;
    }

    return target ? createPortal(content, target) : null;
};

export default CommonPagination;